import { Component,ChangeDetectorRef } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import jsPDF from 'jspdf';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ConfirmationService } from 'primeng/api';

// PrimeNG modules
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { AutoCompleteModule } from "primeng/autocomplete";
import { RadioButtonModule } from 'primeng/radiobutton';

import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';
import { AutofocusDirective } from '../../../component/common/autofocus.directive';

@Component({
  selector: 'app-certificates',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    DropdownModule,
    EditorModule,
    ButtonModule,
    TabViewModule,
    InputTextModule,
    InputTextareaModule,
    CalendarModule,
    AutoCompleteModule,
    RadioButtonModule,
    AutofocusDirective
  ],
  templateUrl: './certificates.component.html',
  styleUrl: './certificates.component.scss',
})
export class CertificatesComponent {
  // track which tab is currently active so preview updates the correct form
  public activeTab: 'medical' | 'travel' | 'hospital' = 'medical';
  medicalForm!: FormGroup;
  travelForm!: FormGroup;
  hospitalForm!: FormGroup;

  medicalTypes = [
    {
      label: 'Medical Examination Certificate',
      value: 'medical examination certificate',
    },
    {
      label: 'General Fitness Certificate',
      value: 'general fitness certificate',
    },
    { label: 'Unfitness Certificate', value: 'unfitness certificate' },
    { label: 'Medical Leave Certificate', value: 'medical leave certificate' },
    {
      label: 'Medical Leave Extension Certificate',
      value: 'medical leave extension certificate',
    },
    {
      label: 'Return to Work / Resume Duty Certificate',
      value: 'return to work certificate',
    },
    {
      label: 'Chronic Illness Certificate',
      value: 'chronic illness certificate',
    },
  ];

  travelTypes = [
    {
      label: 'Fitness for Travel Certificate',
      value: 'fitness for travel certificate',
    },
    {
      label: 'Sports / Adventure Fitness Certificate',
      value: 'sports adventure fitness certificate',
    },
  ];

  specialTypes = [
    { label: 'Pregnancy Certificate', value: 'pregnancy certificate' },
    { label: 'Vaccination Certificate', value: 'vaccination certificate' },
    {
      label: 'Injury / Accident Certificate',
      value: 'injury accident certificate',
    },
  ];

  hospitalTypes = [
    { label: 'Discharge Certificate', value: 'discharge certificate' },
  ];

  templates: Record<string, string> = {
    'medical examination certificate': `
  This is to certify that the individual was examined on the above-mentioned date. Based on clinical evaluation, the general health status of the individual has been assessed. This certificate is issued upon request for official purposes such as employment, education, or insurance.`,

    'general fitness certificate': `
  This is to certify that the individual has been medically examined and is found to be physically fit, with no medical contraindications at the time of examination, to perform routine professional or daily activities.`,

    'unfitness certificate': `
  This is to certify that the individual was examined and is presently unfit to perform routine work or academic activities due to medical reasons. Rest and medical care have been advised as deemed necessary.`,

    'medical leave certificate': `
  This is to certify that the individual was under medical care and was advised rest due to illness. The individual was medically unfit to attend work or school during the advised period.`,

    'medical leave extension certificate': `
  This is to certify that upon re-evaluation, the individual continues to require medical rest. The medical leave previously granted has been extended based on current clinical findings.`,

    'return to work certificate': `
  This is to certify that the individual has recovered satisfactorily and is medically fit to resume work or academic activities from the specified date.`,

    'chronic illness certificate': `
  This is to certify that the individual is diagnosed with a chronic medical condition and is under ongoing medical care. This certificate is issued for official purposes requiring medical confirmation.`,

    'fitness for travel certificate': `
  This is to certify that the individual has been medically examined and is found fit to undertake travel by the specified mode, with no current medical condition likely to compromise safety during travel.`,

    'sports adventure fitness certificate': `
  This is to certify that the individual has undergone a medical examination and is deemed fit for participation in sports or adventure activities. Issued based on clinical examination only. No responsibility is accepted for injury during activity.`,
  };

  previewData: any = null;

  // logo = '/logo.png'; // <-- for local
  logo = '/clinic/logo.png'; // <-- for server

  private apiUrl: string | undefined;
  public patientsList: any[] = [];

  public selectedPatient: any = null;

  constructor(
    private fb: FormBuilder, 
    private http: HttpClient, 
    private apiService: ApiService,
    private toast: ToasterService,
    // private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
   }

  ngOnInit(): void {
    // Initialize forms
    this.medicalForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Shailesh Sancheti', Validators.required],
      remarks: [''],
      certificateType: ['medical examination certificate', Validators.required],
      certificateBody: [this.templates['medical examination certificate']],
      paperSize: ['a5']
    });

    this.travelForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Shailesh Sancheti', Validators.required],
      remarks: [''],
      certificateType: ['fitness for travel certificate', Validators.required],
      certificateBody: [this.templates['fitness for travel certificate']],
      paperSize: ['a5']
    });

    this.hospitalForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Shailesh Sancheti', Validators.required],
      remarks: [''],
      certificateType: ['discharge certificate', Validators.required],
      certificateBody: [this.templates['discharge certificate']],
      paperSize: ['a5']
    });

    // Live preview
    this.medicalForm.valueChanges.subscribe(() => {
      this.updatePreview('medical');
    });

    this.travelForm.valueChanges.subscribe(() => {
      this.updatePreview('travel');
    });

    this.hospitalForm.valueChanges.subscribe(() => {
      this.updatePreview('hospital');
    });
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders() })
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patientsList = [];
          this.toast.add({
            severity: 'info',
            summary: 'Info',
            detail: `No patients found for "${query}".`
          });
        } else {
          this.patientsList = res.data.map((p: any) => ({
            ...p,
            full_name: `${p.first_name} ${p.middle_name} ${p.last_name} (${p.patient_number})`
          }));
        }
        // Stop spinner and update view
        this.cdr.detectChanges();
      }, error => {
        this.patientsList = [];
        this.toast.add({
          severity: 'info',
          summary: 'Info',
          detail: 'No patients found.'
        });
        this.cdr.detectChanges();
      });
  }

  // onPatientSelect(event: any) {
  //   this.selectedPatient = event.value;
  // }

  onPatientSelect(event: any) {
    const patient = event.value;
    this.selectedPatient = patient;
    if (!patient) return;

    // build full name
    const fullName = [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(' ');

    // compute age from DOB if available, fallback to provided age fields
    let age: number | string = '';
    if (patient.date_of_birth) {
      const dob = new Date(patient.date_of_birth);
      let years = new Date().getFullYear() - dob.getFullYear();
      const m = new Date().getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && new Date().getDate() < dob.getDate())) years--;
      age = years;
    } else if (patient.age_years ?? patient.age) {
      age = patient.age_years ?? patient.age;
    }

    // normalize gender (depends on API: 'gender' or 'sex')
    const gender = patient.gender ?? patient.sex ?? '';

    // patch medical form (control names: patientName, age, gender)
    this.medicalForm.patchValue({
      patientName: fullName,
      age,
      gender
    });

    this.travelForm.patchValue({
      patientName: fullName,
      age,
      gender
    });

    // update live preview for the active tab
    this.updatePreview(this.activeTab);
  }

  onTabChange(event: any) {
    const idx = event?.index ?? 0;
    this.activeTab = idx === 0 ? 'medical' : idx === 1 ? 'travel' : 'hospital';
    this.updatePreview(this.activeTab);
  }

  // Quill / p-editor emits text-change events; sync editor HTML to form control
  onEditorTextChange(event: any, tab: 'medical' | 'travel' | 'hospital') {
    // event payload varies; try common properties in order
      const html = event?.html ?? event?.htmlValue ?? event?.editor?.root?.innerHTML ?? '';
      const form = tab === 'medical' ? this.medicalForm : tab === 'travel' ? this.travelForm : this.hospitalForm;
      if (!form) return;

      // Avoid writing back into the form control (this causes Quill to reset cursor to start).
      // Instead update the live preview directly using the editor HTML.
      const f = { ...form.value, certificateBody: html };
      this.previewData = {
        ...f,
        dateFormatted: f.date
          ? new Date(f.date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          : '',
      };
      this.cdr.detectChanges();
  }

  updatePreview(tab: 'medical' | 'travel' | 'hospital') {
    let form: FormGroup;

    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else form = this.hospitalForm;

    const f = form.value;

    this.previewData = {
      ...f,
      dateFormatted: f.date
        ? new Date(f.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
        : '',
    };
  }

  // Update template when type changes
  onTypeChange(tab: 'medical' | 'travel' | 'hospital') {
    let form: FormGroup;

    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else form = this.hospitalForm;

    const type = form.get('certificateType')?.value;
    form.get('certificateBody')?.setValue(this.templates[type] || '');

    this.updatePreview(tab);
  }

  // Generate PDF
  generatePDFlocally(tab: 'medical' | 'travel' | 'hospital') {
    let form: FormGroup;
    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else form = this.hospitalForm;

    const f = form.value;


    // Check paper size from form
    const paperSize = f.paperSize || 'a5';

    // Default to A5 config
    let format = 'a5';
    let orientation: 'p' | 'l' = 'p';

    if (paperSize === 'a4') {
        format = 'a4';
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: format,
    });

    // Add logo
    const img = new Image();
    // img.src = '/logo.png'; //! <--- For local
    img.src = '/clinic/logo.png'; //! <--- For server

    if (paperSize === 'a4') {
        img.onload = () => this.buildPDFA4(doc, f, img);
        img.onerror = () => this.buildPDFA4(doc, f);
    } else {
        img.onload = () => this.buildPDFA5(doc, f, img);
        img.onerror = () => this.buildPDFA5(doc, f);
    }

    // OLD CODE
    // const doc = new jsPDF({ unit: 'pt', format: 'a4' });

    // // Add logo
    // const img = new Image();
    // // img.src = '/logo.png'; //! <--- For local
    // img.src = '/clinic/logo.png'; //! <--- For server
    // img.onload = () => this.buildPDF(doc, f, img);
    // img.onerror = () => this.buildPDF(doc, f);
  }

  // Build PDF A4 content
  private buildPDFA4(doc: jsPDF, f: any, img?: HTMLImageElement) {

      /* ================= BASE CONFIG ================= */

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const marginX = 15; // Reduced from 20 for standard print margin
      let y = 15; // Start at 15mm

      // Brand colors
      const brandPink: [number, number, number] = [227, 28, 121];   // #E31C79
      const brandOrange: [number, number, number] = [246, 137, 32]; // #F68920
      const darkGray: [number, number, number] = [51, 51, 51];      // #333

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkGray);

      /* ================= HEADER ================= */

      const headerStartY = y;

      if (img) {
        // Image takes 25% width (slightly smaller than A5 prop)
        const imgWidth = (pageWidth - marginX * 2) * 0.25;
        const imgHeight = imgWidth * 0.4; // Maintain aspect ratio
        doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
      } else {
        doc.setFillColor(...brandPink);
        doc.roundedRect(marginX, y, 35, 35, 3, 3, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('CLINIC', marginX + 17.5, y + 15, { align: 'center' });
        doc.text('LOGO', marginX + 17.5, y + 23, { align: 'center' });
      }

      // Header text 
      const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.25 + 12 : 50);

      doc.setTextColor(...darkGray);
      doc.setFont('helvetica', 'bold');
      
      doc.setFontSize(10); 
      const lineHeight = 4.5;

      doc.text(
        'Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,',
        headerTextX,
        y + 8
      );
      doc.text(
        'Maharshi Nagar, Pune, Maharashtra – 411037',
        headerTextX,
        y + 8 + lineHeight
      );
      doc.text(
        'Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com',
        headerTextX,
        y + 8 + (lineHeight * 2)
      );

      y += 25; // Compacted from 70

      // Border bottom with gradient effect
      doc.setLineWidth(2);
      doc.setDrawColor(...brandPink);
      doc.line(marginX, y, pageWidth / 2, y);
      doc.setDrawColor(...brandOrange);
      doc.line(pageWidth / 2, y, pageWidth - marginX, y);

      y += 2.5;

      /* ================= SUB HEADER ================= */

      const halfWidth = (pageWidth - marginX * 2) / 2;
      const subHeaderHeight = 10; // Compact 10mm

      doc.setFillColor(...brandPink);
      doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');

      doc.setFillColor(...brandOrange);
      doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8); 
      doc.setTextColor(255, 255, 255);

      doc.text(
        'SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES',
        marginX + 8,
        y + 6.5,
      );

      doc.text(
        'Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm',
        pageWidth - marginX - 8,
        y + 6.5,
        { align: 'right' }
      );

      y += subHeaderHeight + 15; // Reasonable gap

      /* ================= PATIENT INFO (2-COLUMN GRID) ================= */

      const col1X = marginX;
      const col2X = pageWidth / 2 + 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...darkGray);
      
      // Row 1
      doc.text('Patient Name:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.patientName || '', col1X + 35, y);

      doc.setFont('helvetica', 'bold');
      doc.text('Date of Examination:', col2X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(
        f.date
          ? new Date(f.date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          : '',
        col2X + 45,
        y
      );

      y += 8;

      // Row 2
      doc.setFont('helvetica', 'bold');
      doc.text('Age:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`${f.age || ''} Years`, col1X + 35, y);

      doc.setFont('helvetica', 'bold');
      doc.text('Sex:', col2X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.gender || '', col2X + 45, y);

      y += 25; // Gap before title

      /* ================= CERTIFICATE TYPE (CENTERED) ================= */

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(...darkGray);
      doc.text((f.certificateType || 'Medical Certificate').toUpperCase(), pageWidth / 2, y, { align: 'center' });

      y += 15;

      /* ================= CERTIFICATE BODY ================= */

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = f.certificateBody || '';
      const bodyText = tempDiv.textContent?.replace(/\s+/g, ' ').trim() || '';

      if (bodyText) {
        const textWidth = pageWidth - marginX * 2;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(13); // Good size for A4

        const splitText = doc.splitTextToSize(bodyText, textWidth);

        doc.setTextColor(...darkGray);

        // Line height 1.4 for A4 is comfortable
        splitText.forEach((line: string, index: number) => {
          doc.text(line, marginX, y + (index * 9));
        });

        y += splitText.length * 11 * 1.4 + 15;
      }

      /* ================= REMARKS SECTION ================= */

      if (f.remarks) {
        const textWidth = pageWidth - marginX * 2;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...darkGray);
        doc.text('Remarks:', marginX, y);

        y += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);

        const remarksText = f.remarks.replace(/\s+/g, ' ').trim();
        const splitRemarks = doc.splitTextToSize(remarksText, textWidth);

        doc.setTextColor(...darkGray);

        splitRemarks.forEach((line: string, index: number) => {
          doc.text(line, marginX, y + (index * 10 * 1.4));
        });

        y += splitRemarks.length * 10 * 1.4 + 15;
      }

      /* ================= FOOTER SIGNATURE (RIGHT ALIGNED) ================= */

      const footerY = Math.max(y + 30, pageHeight - 40);

      const sigWidth = 70;
      const sigX = pageWidth - marginX - sigWidth;

      doc.setDrawColor(...darkGray);
      doc.setLineWidth(1);
      doc.line(sigX, footerY, pageWidth - marginX, footerY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(...darkGray);
      doc.text('Dr. Shailesh Sancheti', sigX, footerY + 8);

      doc.setFontSize(9);
      doc.text('MD (Homeopathy)', sigX, footerY + 14);

      /* ================= SAVE ================= */

      const rawType = (f.certificateType || 'medical certificate').toString();
      const rawName = (f.patientName || 'patient').toString();

      const slug = (s: string) =>
        s
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_\-]/g, '')
          .replace(/_+/g, '_');

      const fileType = slug(rawType);
      const fileNamePart = slug(rawName);

      const fileName = `${fileType}_${fileNamePart || 'patient'}.pdf`;

      doc.save(fileName);
  }

  // A5
  private buildPDFA5(doc: jsPDF, f: any, img?: HTMLImageElement) {

      /* ================= BASE CONFIG ================= */

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const marginX = 12; 
      let y = 10; // Start at 10mm top margin

      // Brand colors
      const brandPink: [number, number, number] = [227, 28, 121];   // #E31C79
      const brandOrange: [number, number, number] = [246, 137, 32]; // #F68920
      const darkGray: [number, number, number] = [51, 51, 51];      // #333

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkGray);

      /* ================= HEADER ================= */

      if (img) {
        // Image takes 30% width
        const imgWidth = (pageWidth - marginX * 2) * 0.3;
        const imgHeight = imgWidth * 0.4; // Maintain aspect ratio
        doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
      } else {
        doc.setFillColor(...brandPink);
        doc.roundedRect(marginX, y, 22, 22, 2, 2, 'F'); 
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text('CLINIC', marginX + 11, y + 9, { align: 'center' });
        doc.text('LOGO', marginX + 11, y + 15, { align: 'center' });
      }

      // Header text
      const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.3 + 6 : 30);

      doc.setTextColor(...darkGray);
      doc.setFont('helvetica', 'bold');
      
      // Address lines - Compact 7pt
      doc.setFontSize(7);
      const lineHeight = 3.5;
      
      doc.text(
        'Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,',
        headerTextX,
        y + 5
      );
      doc.text(
        'Maharshi Nagar, Pune, Maharashtra – 411037',
        headerTextX,
        y + 5 + lineHeight
      );
      doc.text(
        'Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com',
        headerTextX,
        y + 5 + (lineHeight * 2)
      );

      // Reduce gap between header and divider
      y += 20; 

      // Border bottom
      doc.setLineWidth(1); 
      doc.setDrawColor(...brandPink);
      doc.line(marginX, y, pageWidth / 2, y);
      doc.setDrawColor(...brandOrange);
      doc.line(pageWidth / 2, y, pageWidth - marginX, y);

      y += 1.5;

      /* ================= SUB HEADER ================= */

      const halfWidth = (pageWidth - marginX * 2) / 2;
      const subHeaderHeight = 7; // Extremely compact

      doc.setFillColor(...brandPink);
      doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');

      doc.setFillColor(...brandOrange);
      doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6); // 6pt
      doc.setTextColor(255, 255, 255);

      doc.text(
        'SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES',
        marginX + 3,
        y + 4.5,
      );

      doc.text(
        'Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm',
        pageWidth - marginX - 3,
        y + 4.5,
        { align: 'right' }
      );

      y += subHeaderHeight + 6; 

      /* ================= PATIENT INFO (2-COLUMN GRID) ================= */

      const col1X = marginX;
      const col2X = pageWidth / 2 + 5;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5); // Reduced to 9pt
      doc.setTextColor(...darkGray);
      
      // Row 1
      doc.text('Patient Name:', col1X, y+3);
      doc.setFont('helvetica', 'normal');
      doc.text(f.patientName || '', col1X + 25, y+3);

      doc.setFont('helvetica', 'bold');
      doc.text('Date of Examination:', col2X, y+3);
      doc.setFont('helvetica', 'normal');
      doc.text(
        f.date
          ? new Date(f.date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          : '',
        col2X + 33,
        y+3
      );

      y += 5; // Tighter row spacing

      // Row 2
      doc.setFont('helvetica', 'bold');
      doc.text('Age:', col1X, y+3);
      doc.setFont('helvetica', 'normal');
      doc.text(`${f.age || ''} Years`, col1X + 25, y+3);

      doc.setFont('helvetica', 'bold');
      doc.text('Sex:', col2X, y+3);
      doc.setFont('helvetica', 'normal');
      doc.text(f.gender || '', col2X + 33, y+3);

      y += 18; // Gap before title

      /* ================= CERTIFICATE TYPE (CENTERED) ================= */

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12); 
      doc.setTextColor(...darkGray);
      doc.text((f.certificateType || 'Medical Certificate').toUpperCase(), pageWidth / 2, y, { align: 'center' });

      y += 8; // Gap before body

      /* ================= CERTIFICATE BODY ================= */

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = f.certificateBody || '';
      const bodyText = tempDiv.textContent?.replace(/\s+/g, ' ').trim() || '';

      if (bodyText) {
        const textWidth = pageWidth - marginX * 2;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10); 

        const splitText = doc.splitTextToSize(bodyText, textWidth);

        doc.setTextColor(...darkGray);

        // Reduced line height to 1.2
        splitText.forEach((line: string, index: number) => {
          doc.text(line, marginX, y + (index * 6)); 
        });

        y += splitText.length * 10 * 1 + 6;
      }

      /* ================= REMARKS SECTION ================= */

      if (f.remarks) {
        const textWidth = pageWidth - marginX * 2;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...darkGray);
        doc.text('Remarks:', marginX, y);

        y += 5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);

        const remarksText = f.remarks.replace(/\s+/g, ' ').trim();
        const splitRemarks = doc.splitTextToSize(remarksText, textWidth);

        doc.setTextColor(...darkGray);

        splitRemarks.forEach((line: string, index: number) => {
          doc.text(line, marginX, y + (index * 9 * 1.2));
        });

        y += splitRemarks.length * 9 * 1.2 + 6;
      }

      /* ================= FOOTER SIGNATURE (RIGHT ALIGNED) ================= */

      // Move Footer UP significantly. 
      // If content is short, footer shouldn't float in middle, closer to bottom but SAFE.
      // pageHeight for A5 is 210mm. 
      // Safe zone > 35mm from bottom to avoid cutting.
      
      const minFooterY = pageHeight - 40; 
      
      // If y (content end) is less than minFooterY, us minFooterY. 
      // If y is greater, push it down but warn/check overflow? 
      // Usually we just overlay or push to next page (but here simple one pager).
      
      // Let's stick to sticking it to bottom area if space allows.
      const footerY = Math.max(y + 10, minFooterY);

      const sigWidth = 45;
      const sigX = pageWidth - marginX - sigWidth;

      doc.setDrawColor(...darkGray);
      doc.setLineWidth(0.5);
      doc.line(sigX, footerY, pageWidth - marginX, footerY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...darkGray);
      doc.text('Dr. Shailesh Sancheti', sigX, footerY + 5);

      doc.setFontSize(7);
      doc.text('MD (Homeopathy)', sigX, footerY + 9);

      /* ================= SAVE ================= */

      const rawType = (f.certificateType || 'medical certificate').toString();
      const rawName = (f.patientName || 'patient').toString();

      const slug = (s: string) =>
        s
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_\-]/g, '')
          .replace(/_+/g, '_');

      const fileType = slug(rawType);
      const fileNamePart = slug(rawName);

      const fileName = `${fileType}_${fileNamePart || 'patient'}.pdf`;

      doc.save(fileName);
  }

  // Generate Laravel PDF
  generatePDF(tab: 'medical' | 'travel' | 'hospital') {

    let form: FormGroup;

    if (tab === 'medical') {
      form = this.medicalForm;
    } else if (tab === 'travel') {
      form = this.travelForm;
    } else {
      form = this.hospitalForm;
    }

    const f = form.value;

    // prepare payload for Laravel
    const payload = {
      patientName: f.patientName,
      age: f.age,
      gender: f.gender,
      date: f.date,
      doctorName: f.doctorName,
      certificateType: f.certificateType,
      certificateBody: f.certificateBody,
      remarks: f.remarks
    };

    this.apiService.generateCertificate(payload)
      .subscribe((blob: any) => {

        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL);

        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Certificate PDF generated and opened.'
        });

      }, (error: any) => {

        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate certificate PDF.'
        });

      });
  }


  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    }
  }
}
